import * as Api from '@ViewModels';
import { useQuery } from '@tanstack/react-query';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { reactQueryClient } from '../../../web/ReactQueryProvider';

const BASE_QUERY_KEY = 'automationTemplate/<id>/contact/<contactId>/steps';

/**
 * Represents a single automation step with its execution status for a specific contact.
 *
 * This interface combines step configuration with runtime execution state, showing
 * how a specific contact is progressing through an automation's steps.
 *
 * Used by:
 * - Contact step history views
 * - Automation testing/preview interfaces
 * - Contact journey tracking
 *
 * @example
 * ```typescript
 * const stepStatus: IAutomationStepStatus = {
 *   step: {
 *     id: 'step-123',
 *     stepType: Api.AutomationStepType.Email,
 *     name: 'Welcome Email'
 *   },
 *   status: { status: 'completed' },
 *   dueDate: '2025-11-21T10:00:00Z',
 *   isFinished: true,
 *   sender: { userId: '456' },
 *   id: 'step-status-789',
 *   _type: 'EmailStepStatus'
 * };
 * ```
 */
export interface IAutomationStepStatus {
	/** The automation step configuration */
	step: Api.IAutomationStep;
	/** Execution status information (pending, completed, failed, etc.) */
	status: {
		/** Status string indicating current state */
		status: string;
	};
	/** ISO 8601 timestamp when this step is/was due to execute */
	dueDate: string;
	/** Whether this step has completed execution */
	isFinished: boolean;
	/** User or system that sent/executed this step (structure varies by step type) */
	sender: unknown;
	/** Unique identifier for this step status record */
	id: string;
	/** Type discriminator for the step status (e.g., 'EmailStepStatus', 'TextingStepStatus') */
	_type: string;
}

/**
 * Fetches the automation steps and their execution status for a specific contact.
 *
 * This query returns the list of steps a contact has gone through (or will go through)
 * in an automation, along with execution status, completion state, and due dates.
 *
 * Use cases:
 * - Showing a contact's progress through an automation
 * - Testing automation flows with real or sample contacts
 * - Viewing step history and timing for a contact
 * - Debugging why a contact is/isn't receiving certain steps
 *
 * Draft vs Published:
 * - useDraft=true: Shows steps from the draft version (for testing before publish)
 * - useDraft=false: Shows steps from the published version (what's actually running)
 *
 * @param options - Query configuration
 * @param options.templateId - Automation template ID
 * @param options.contactId - Contact ID to get steps for
 * @param options.useDraft - Whether to use draft steps or published steps (default: false)
 * @param options.enabled - Whether to enable the query (default: true)
 * @param options.impersonationContext - Optional impersonation context for admin users
 * @returns React Query result with array of step status objects
 *
 * @example
 * ```typescript
 * const { data: contactSteps, isLoading } = useAutomationTemplateContactStepsQuery({
 *   templateId: 'template-123',
 *   contactId: 'contact-456',
 *   useDraft: true, // Preview draft version
 *   enabled: Boolean(templateId && contactId)
 * });
 *
 * // Display contact's step history:
 * contactSteps?.map(stepStatus => (
 *   <StepHistoryItem
 *     key={stepStatus.id}
 *     step={stepStatus.step}
 *     status={stepStatus.status.status}
 *     completed={stepStatus.isFinished}
 *     dueDate={stepStatus.dueDate}
 *   />
 * ));
 * ```
 *
 * @example
 * ```typescript
 * // Check if a contact has completed a specific step:
 * const { data } = useAutomationTemplateContactStepsQuery({
 *   templateId,
 *   contactId
 * });
 *
 * const hasCompletedWelcomeEmail = data?.some(
 *   s => s.step.name === 'Welcome Email' && s.isFinished
 * );
 * ```
 */
export function useAutomationTemplateContactStepsQuery({
	templateId,
	contactId,
	useDraft = false,
	enabled = true,
	impersonationContext,
}: {
	templateId: string;
	contactId: string;
	useDraft?: boolean;
	enabled?: boolean;
	impersonationContext?: Api.IImpersonationContext;
}) {
	const userSession = useUserSession();

	return useQuery({
		enabled: enabled && !!templateId && !!contactId,
		queryFn: () => {
			return userSession.webServiceHelper.callAsync<IAutomationStepStatus[]>(
				Api.ImpersonationBroker.composeApiUrl({
					urlPath: `automationtemplate/${encodeURIComponent(templateId)}/contact/${encodeURIComponent(contactId)}/steps`,
					queryParams: { useDraft },
					impersonationContext,
				}),
				'GET'
			);
		},
		queryKey: [BASE_QUERY_KEY, templateId, contactId, useDraft, impersonationContext],
		// Contact steps may change as automations run, so use a shorter staleTime of 2 minutes.
		// This balances keeping data reasonably fresh while reducing unnecessary server requests.
		staleTime: 2 * 60 * 1000, // 2 minutes
		// Keep in cache for 5 minutes after unmounting to support quick navigation patterns.
		gcTime: 5 * 60 * 1000, // 5 minutes
		// Don't refetch on window focus - step status changes are driven by user actions, not external updates.
		refetchOnWindowFocus: false,
	});
}

/**
 * Invalidates cached contact step queries for a specific template and contact.
 *
 * Call this after actions that change a contact's step status or progression
 * through an automation to ensure the UI shows the latest data.
 *
 * Common use cases:
 * - After manually advancing a contact to the next step
 * - After marking a step as complete/incomplete
 * - After resetting a contact's automation progress
 * - After publishing template changes that affect this contact
 *
 * @param templateId - Automation template ID
 * @param contactId - Contact ID whose steps should be refetched
 *
 * @example
 * ```typescript
 * const advanceContactMutation = useMutation({
 *   mutationFn: advanceContactToNextStep,
 *   onSuccess: () => {
 *     invalidateAutomationTemplateContactStepsQueries(templateId, contactId);
 *   }
 * });
 * ```
 */
export function invalidateAutomationTemplateContactStepsQueries(templateId: string, contactId: string) {
	reactQueryClient.invalidateQueries({
		queryKey: [BASE_QUERY_KEY, templateId, contactId],
	});
}
